/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { v4 } from "uuid";
import useClickOutside from "../hook/useClickOutside";

const Wrapper = styled.div<{ noLabel: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  height: ${(props) => (props.noLabel ? 210 : 266)}px;
`;

const DateLabel = styled.div<{ now?: boolean }>`
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  color: rgb(47, 47, 47);
  width: fit-content;
  margin-top: 5px;
  padding: 5px;
  border-radius: 2px;
  height: 28px;
  margin-bottom: 5px;

  :hover {
    color: #fb972e;
    background-color: #fb972e40;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  margin: 0 auto auto auto;
  width: 280px !important;
  max-height: 100px !important;
`;

const Row = styled.div<{ interval: 5 | 15 }>`
  display: flex;
  align-items: center;
  justify-content: ${({ interval }) =>
    interval === 5 ? "space-between" : "center"};
  width: 100%;
`;

interface TimeItemProps {
  disabled?: boolean;
  isNow: boolean;
  selected: boolean;
}

const TimeItem = styled.div<TimeItemProps>`
  display: flex;
  margin: 2px 0px;
  padding: 5px 5px;
  width: 80px;
  font-size: 13px;
  justify-content: center;
  border: 1px solid white;
  font-family: "Roboto", sans-serif;
  cursor: default;
  border-radius: 2px;
  background-color: #fff;
  color: rgb(85, 89, 92);

  ${(props) => {
    if (props.isNow) {
      return `
        background-color: #f0f0f0;
        font-weight: 600;
        `;
    }
  }};

  ${(props) => {
    if (props.selected) {
      return `
        border: 1px solid #fb972e;
        color: #fb972e;
        `;
    }
  }};

  ${(props) => {
    if (props.disabled) {
      return `opacity: 0.2`;
    }
  }};

  &:hover {
    color: ${(props) => !props.disabled && "#fb972e"};
    cursor: ${(props) => !props.disabled && "pointer"};
    background-color: ${(props) => !props.disabled && "#fb972e20"};
    font-weight: ${(props) => !props.disabled && "600"};
  }
`;
interface ITime {
  hour?: number;
  minute?: number;
  period?: "am" | "pm";
}
interface IHourItem {
  hour: {
    label: string;
    value: number;
  };
  selected: boolean;
  isNow: boolean;
}

interface IMinuteItem {
  minute: {
    label: string;
    value: number;
  };
  isNow: boolean;
  selected: boolean;
}

interface Props {
  minuteInterval: 5 | 15;
  date: Date | undefined;
  noLabel?: boolean;
  favoriteTime?: boolean;
  onSelect: (date: ITime) => void;
  goBack: () => void;

  step: "hour" | "minute";
  setStep: (props: "hour" | "minute") => void;
}

export default function StyledTimePìcker(props: Props): JSX.Element {
  const {
    onSelect,
    date,
    goBack,
    noLabel,
    minuteInterval,
    step,
    setStep,
    favoriteTime,
  } = props;
  const [hourMatrix, setHourMatrix] = useState<IHourItem[][]>([[]]);
  const [minutesMatrix, setMinutesMatrix] = useState<IMinuteItem[][]>([[]]);
  const [time, setTime] = useState<ITime | undefined>();
  const now = dayjs(noLabel ? new Date().setHours(0) : undefined);

  const buildHourMatrix = useCallback(() => {
    const rowArray = [];
    const rowsArray = [];

    for (let i = 0; i < 24; i += 1) {
      const hour = i % 12 === 0 ? 12 : i;
      if (i % 4 === 0 && i !== 0) {
        rowsArray.push([...rowArray]);
        // This empties the array
        rowArray.length = 0;
      }

      rowArray.push({
        isNow: i === now.hour(),
        selected: false,
        hour: {
          label: `${hour % 12 === 0 ? 12 : hour % 12}:00 ${
            i < 12 ? "am" : "pm"
          }`.padStart(2, "0"),
          value: i,
        },
      });
    }
    rowsArray.push([...rowArray]);

    setHourMatrix([...rowsArray]);
  }, []);

  const buildMinutesMatrix = useCallback(
    ({ hour, period }: ITime) => {
      const minutesArray = [];
      const minutesRowArray: IMinuteItem[] = [];

      for (let i = 0; minuteInterval === 5 ? i < 12 : i < 4; i += 1) {
        const isNow =
          (i + 1) * minuteInterval >= now.minute() &&
          i * minuteInterval < now.minute();
        if (i % (minuteInterval === 5 ? 4 : 2) === 0 && i !== 0) {
          minutesArray.push([...minutesRowArray]);
          minutesRowArray.length = 0;
        }
        const labeledHour =
          (((hour as number) % 12 === 0 ? 12 : hour) as number) % 12;

        minutesRowArray.push({
          selected: false,
          minute: {
            label: `${labeledHour === 0 ? 12 : labeledHour}:${`${
              i * minuteInterval
            }`.padStart(2, "0")} ${period}`,
            value: i * minuteInterval,
          },
          isNow,
        });
      }
      minutesArray.push([...minutesRowArray]);

      setMinutesMatrix(minutesArray);
    },
    [minuteInterval]
  );

  useEffect(() => {
    if (time) buildMinutesMatrix(time);
  }, [minuteInterval]);

  function onHourPick(chosenHour: number) {
    const hourTime = {
      ...time,
      hour: chosenHour,
      period: (chosenHour >= 12 ? "pm" : "am") as "am" | "pm",
    };

    setTime(hourTime);
    setStep("minute");
    buildMinutesMatrix(hourTime);
  }

  function onMinutePick(chosenTime: number) {
    const newTime = {
      ...time,
      minute: chosenTime,
    };
    setTime(newTime as ITime);
    onSelect(newTime as ITime);
  }

  useEffect(() => {
    buildHourMatrix();
  }, [buildHourMatrix]);

  function formatLabel() {
    const formated = date ? dayjs(date).format("MMM D, YYYY") : "--- --, ----";
    let label = `${formated}`;

    if (time) {
      label += ` - ${time.hour === 0 ? 12 : (time.hour || 13) % 12}:${
        time.minute || "00"
      } ${time.period === "am" ? "am" : "pm"}`;
    }

    if (favoriteTime && step === "hour") {
      label = "Choose time";
    }

    if (favoriteTime && step === "minute" && time) {
      label = `${time.hour === 0 ? 12 : (time.hour || 13) % 12}:${
        time.minute || "00"
      } ${time.period === "am" ? "am" : "pm"}`;
    }

    return label;
  }

  const ref = useClickOutside(() => {
    if (noLabel) goBack();
  });

  return (
    <Wrapper ref={ref} noLabel={noLabel}>
      {!noLabel && (
        <DateLabel
          onClick={() => {
            goBack();
          }}
        >
          {formatLabel()}
        </DateLabel>
      )}
      {step === "hour" ? (
        <Container>
          {hourMatrix.map((hourColumn) => {
            return (
              <Row key={v4()} interval={5}>
                {hourColumn.map((hour) => {
                  const disabled = favoriteTime
                    ? false
                    : dayjs(date).isSame(now, "day") &&
                      (now.hour() > hour.hour.value ||
                        (now.hour() === hour.hour.value && now.minute() > 54));
                  return (
                    <TimeItem
                      selected={
                        dayjs(date).hour() === hour.hour.value &&
                        dayjs(date).minute() !== 21
                      }
                      disabled={disabled}
                      key={v4()}
                      isNow={hour.isNow}
                      onClick={() => {
                        if (!disabled) onHourPick(hour.hour.value);
                      }}
                    >
                      {hour.hour.label}
                    </TimeItem>
                  );
                })}
              </Row>
            );
          })}
        </Container>
      ) : (
        <Container>
          {minutesMatrix.map((minutesRow) => {
            return (
              <Row key={v4()} interval={minuteInterval}>
                {minutesRow.map((minute) => {
                  const disabled = favoriteTime
                    ? false
                    : dayjs(date).isSame(now, "day") &&
                      time?.hour === now.hour() &&
                      now.minute() >= minute.minute.value;

                  return (
                    <TimeItem
                      selected={
                        time?.hour === dayjs(date).hour() &&
                        dayjs(date).minute() === minute.minute.value
                      }
                      disabled={disabled}
                      key={v4()}
                      isNow={time?.hour === now.hour() && minute.isNow}
                      onClick={() => {
                        if (!disabled) onMinutePick(minute.minute.value);
                      }}
                    >
                      {minute.minute.label}
                    </TimeItem>
                  );
                })}
              </Row>
            );
          })}
        </Container>
      )}
    </Wrapper>
  );
}
